import { Injectable } from '@angular/core';
import { getCurrentUser, GetCurrentUserOutput, fetchAuthSession } from '@aws-amplify/auth';
import { Permission, Role, Roles, User } from '../interfaces/permission';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userRole: Role | null = null;
  private apiUrl = environment.apiUrl + 'verifytoken';
  private spoofedRole: Role | null = null;
  private userPermissions: Permission[] | null = null;

  constructor(private http: HttpClient) {}
  /**
   * Get the user role.
   *
   * Extract the user role from the ID token.
   *
   * @returns Promise containing the user role.
   */
  private async getUserRole(): Promise<Role | null> {
    if (this.userRole) {
      return this.userRole;
    }
    try {
      const response = await axios.get(this.apiUrl, { withCredentials: true });
      const user = response.data.user;

      // Assuming the role is part of the user object returned by the backend
      this.userRole = user.role;
      return this.userRole;
    } catch (error) {
      console.error('Error fetching user role:', error);
      throw new Error('Unable to fetch user role');
    }
  }

  /**
   * Get the user.
   *
   * Fetch the user information from the backend.
   *
   * @returns Observable containing the user information.
   */
  getUser(): Observable<User> {
    return this.http
      .get<{ user: { username: string; activeRole: string; roles: string[]; centerName?: string; centerNames?: string[] } }>(this.apiUrl, { withCredentials: true })
      .pipe(
        map((response) => {
          const { username, activeRole, roles, centerName, centerNames } = response.user;

          // Convert role enum names to Role objects
          const userRoles = roles.map((roleName) => {
            const role = Roles[roleName.toUpperCase()];
            if (!role) {
              console.error(`Invalid role received from backend: ${roleName}`);
            }
            return role;
          }).filter(Boolean) as Role[];

          const activeRoleObj = Roles[activeRole.toUpperCase()];
          if (!activeRoleObj) {
            throw new Error('Invalid active role received from backend');
          }

          return {
            username,
            roles: userRoles,
            activeRole: activeRoleObj,
            centerName: centerName || null,
            centerNames: centerNames || []
          };
        }),
        catchError((error) => {
          console.error('Error fetching user:', error);
          return throwError(() => new Error('Unable to fetch user'));
        })
      );
  }

  getAllPermissions$(): Observable<Permission[]> {
    if (this.userPermissions) {
      return of(this.userPermissions); // Return cached permissions
    }

    return this.getUser().pipe(
      map((user) => {
        this.userPermissions = user.activeRole.permissions; // Cache permissions from active role
        return this.userPermissions;
      }),
      catchError((error) => {
        console.error('Error fetching permissions:', error);
        return of([]); // Return empty permissions on error
      })
    );
  }
  /**
   * Check if the user has all of the provided permissions.
   *
   * The function returns true if the user has all of the provided permissions according
   * to the user role. Should be used for a single permission or multiple AND permissions.
   *
   * @param  {Permission} permissions Permissions to check for.
   *
   * @returns Observable with a boolean indicating whether the user has the permission.
   */
  hasPermissions$(...permissions: Permission[]): Observable<boolean> {
    return from(this.getUser()).pipe(
      map((user) => {
        // Use activeRole permissions (which may be spoofed or switched)
        return permissions.every((permission) => user.activeRole.permissions.includes(permission));
      }),
      catchError((error) => {
        console.error('Error checking permissions:', error);
        return of(false);
      })
    );
  }

  /**
   * Check if the user has any of the provided permissions.
   *
   * The function returns true if the user has any of the provided permissions according
   * to the user role. Should be used for multiple OR permissions.
   *
   * @param  {Permission[]} permissions Permissions to check for.
   *
   * @returns Observable with a boolean indicating whether the user has any of the permissions.
   */
  hasAnyPermission$(...permissions: Permission[]): Observable<boolean> {
    return from(this.getUser()).pipe(
      map((user) => {
        // Use activeRole permissions (which may be spoofed or switched)
        return permissions.some((permission) => user.activeRole.permissions.includes(permission));
      }),
      catchError((error) => {
        console.error('Error checking permissions:', error);
        return of(false);
      })
    );
  }

  /**
   * Log out the user.
   *
   * Clears the local session and redirects the user to the Cognito logout URL.
   */
  logout(): void {
    // Clear local session (if any)
    this.userRole = null;
    this.clearSwitchedRole();
    // Redirect the user to the Cognito logout URL
    window.location.href = environment.logoutUrl;
  }

  /**
   * Get the current role (real or switched).
   *
   * @returns The current role.
   */
  async getCurrentRole(): Promise<Role> {
    const user = await firstValueFrom(this.getUser());
    return user.activeRole;
  }

  /**
   * Set a switched role.
   *
   * @param role The role to switch to.
   */
  setSwitchedRole(role: Role, centerGroup: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}admin/switch-role`, { role: role.enumName, centerGroup }).pipe(
      map(() => {
        this.spoofedRole = role; // Cache the switched role locally
      }),
      catchError((error) => {
        console.error('Error setting switched role:', error);
        return throwError(() => new Error('Unable to set switched role'));
      })
    );
  }
  /**
   * Clear the switched role.
   */
  clearSwitchedRole(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}admin/clear-switched-role`, {}).pipe(
      map(() => {
        this.spoofedRole = null; // Clear the cached switched role locally
      }),
      catchError((error) => {
        console.error('Error clearing switched role:', error);
        return throwError(() => new Error('Unable to clear switched role'));
      })
    );
  }
}
